import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  iQuestion,
  iTicketingSetup,
  tFieldMode,
} from 'services/Ticketing/Ticketing.type';
import { card, sectionTitle } from '../common';

interface VisitorFormSectionProps {
  data: iTicketingSetup;
  form: Omit<iTicketingSetup['settings'], 'logo_url'>;
  set: <K extends keyof Omit<iTicketingSetup['settings'], 'logo_url'>>(
    key: K,
    value: Omit<iTicketingSetup['settings'], 'logo_url'>[K]
  ) => void;
}

export function VisitorFormSection({
  data,
  form,
  set,
}: VisitorFormSectionProps): JSX.Element {
  return (
    <Box sx={{ ...card }}>
      <Typography sx={sectionTitle}>Formulaire visiteur</Typography>
      <Typography sx={{ fontSize: 13, color: '#666', mb: 1 }}>
        Prénom, nom et e-mail sont toujours demandés.
      </Typography>

      {/* Field visibility settings */}
      {Object.entries(data.field_labels).map(([k, label]) => (
        <Box
          key={k}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 0.5,
            borderBottom: '1px solid #f4f4f4',
          }}
        >
          <Typography sx={{ fontSize: 14 }}>{label}</Typography>
          <Select
            size="small"
            value={form.fields[k]}
            onChange={(e) =>
              set('fields', {
                ...form.fields,
                [k]: e.target.value as tFieldMode,
              })
            }
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="hidden">Masqué</MenuItem>
            <MenuItem value="optional">Facultatif</MenuItem>
            <MenuItem value="required">Obligatoire</MenuItem>
          </Select>
        </Box>
      ))}

      {/* Custom questions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 2,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
          Questions personnalisées
        </Typography>
        <Button
          size="small"
          sx={{ textTransform: 'none' }}
          onClick={() =>
            set('questions', [
              ...form.questions,
              {
                id: '',
                label_fr: '',
                label_en: '',
                type: 'text',
                options: [],
                required: false,
              },
            ])
          }
        >
          + Ajouter
        </Button>
      </Box>

      {/* Question list */}
      {form.questions.map((q: iQuestion, i: number) => {
        const upd = (patch: Partial<iQuestion>): void =>
          set(
            'questions',
            form.questions.map((x, j) => (j === i ? { ...x, ...patch } : x))
          );

        return (
          <Box
            key={i}
            sx={{
              border: '1px solid #eee',
              borderRadius: 1,
              p: 1.5,
              mt: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              bgcolor: '#fafafa',
            }}
          >
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                fullWidth
                label="Question (français)"
                value={q.label_fr}
                onChange={(e) => upd({ label_fr: e.target.value })}
              />
              <Select
                size="small"
                value={q.type}
                onChange={(e) =>
                  upd({ type: e.target.value as iQuestion['type'] })
                }
              >
                <MenuItem value="text">Texte</MenuItem>
                <MenuItem value="select">Liste</MenuItem>
                <MenuItem value="checkbox">Case à cocher</MenuItem>
              </Select>
            </Box>
            <TextField
              size="small"
              label="Question (anglais, facultatif)"
              value={q.label_en ?? ''}
              onChange={(e) => upd({ label_en: e.target.value })}
            />
            {q.type === 'select' && (
              <TextField
                size="small"
                label="Choix séparés par des virgules"
                value={q.options.join(', ')}
                onChange={(e) =>
                  upd({
                    options: e.target.value
                      .split(',')
                      .map((o) => o.trimStart()),
                  })
                }
              />
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={q.required}
                    onChange={(e) => upd({ required: e.target.checked })}
                  />
                }
                label="Obligatoire"
              />
              <Button
                size="small"
                color="error"
                sx={{ textTransform: 'none' }}
                onClick={() =>
                  set(
                    'questions',
                    form.questions.filter((_, j) => j !== i)
                  )
                }
              >
                Supprimer
              </Button>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default VisitorFormSection;
